import { useEffect, useState, useRef } from 'react';
import { logout } from './lib/auth';
import { api } from './lib/api';
import { Landing } from './pages/Landing';
import { Signup } from './pages/Signup';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Upload } from './pages/Upload';
import { Settings } from './pages/Settings';
import { ReceiptDetail } from './pages/ReceiptDetail';
import { Onboarding } from './pages/Onboarding';
import { ReviewInbox } from './pages/ReviewInbox';
import { Rules } from './pages/Rules';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Security } from './pages/Security';
import { About } from './pages/About';
import { AppShell } from './components/AppShell';

type Route =
  | { name: 'landing' }
  | { name: 'signup' }
  | { name: 'login' }
  | { name: 'dashboard' }
  | { name: 'upload' }
  | { name: 'review' }
  | { name: 'rules' }
  | { name: 'settings' }
  | { name: 'privacy' }
  | { name: 'terms' }
  | { name: 'security' }
  | { name: 'about' }
  | { name: 'receipt'; id: string };

function parseHash(): Route {
  const hash = window.location.hash.replace(/^#/, '') || '/';
  if (hash.startsWith('/signup')) return { name: 'signup' };
  if (hash.startsWith('/login')) return { name: 'login' };
  if (hash.startsWith('/upload')) return { name: 'upload' };
  if (hash.startsWith('/review')) return { name: 'review' };
  if (hash.startsWith('/rules')) return { name: 'rules' };
  if (hash.startsWith('/settings')) return { name: 'settings' };
  if (hash.startsWith('/privacy')) return { name: 'privacy' };
  if (hash.startsWith('/terms')) return { name: 'terms' };
  if (hash.startsWith('/security')) return { name: 'security' };
  if (hash.startsWith('/about')) return { name: 'about' };
  const m = hash.match(/^\/receipts\/([^/?]+)/);
  if (m?.[1]) return { name: 'receipt', id: m[1] };
  if (hash === '/' || hash === '') return { name: 'dashboard' };
  return { name: 'dashboard' };
}

function parseHashPublic(): Route | null {
  // Public pages (reachable without auth). Signup chooser + legal pages.
  const hash = window.location.hash.replace(/^#/, '') || '/';
  if (hash.startsWith('/signup')) return { name: 'signup' };
  if (hash.startsWith('/privacy')) return { name: 'privacy' };
  if (hash.startsWith('/terms')) return { name: 'terms' };
  if (hash.startsWith('/security')) return { name: 'security' };
  if (hash.startsWith('/about')) return { name: 'about' };
  return null;
}

function renderLegal(route: Route) {
  if (route.name === 'privacy') return <Privacy />;
  if (route.name === 'terms') return <Terms />;
  if (route.name === 'security') return <Security />;
  if (route.name === 'about') return <About />;
  return null;
}

export function App() {
  const [route, setRoute] = useState<Route>({ name: 'landing' });
  const [user, setUser] = useState<{ id: string; email: string; inbox_prefix: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    const onHash = () => {
      // Logged-in: route normally. Logged-out: only re-route for public legal pages
      // so we don't kick them out of the landing page unexpectedly.
      if (userRef.current) {
        setRoute(parseHash());
      } else {
        const pub = parseHashPublic();
        if (pub) setRoute(pub);
        else setRoute({ name: 'landing' });
      }
    };
    window.addEventListener('hashchange', onHash);

    (async () => {
      try {
        const { user: u } = await api.me();
        setUser(u);
        userRef.current = u;
        // Onboarding triggers when the user has neither a Xero NOR MYOB connection
        // with posting defaults set, or when we're mid-OAuth round-trip.
        const [xero, myob] = await Promise.all([
          api.xeroStatus().catch(() => ({ connected: false, connection: null as null | { default_bank_account_id: string | null; default_account_code: string | null } })),
          api.myobStatus().catch(() => ({ connected: false, connection: null as null | { default_bank_account_uid: string | null; default_account_uid: string | null; default_tax_code_uid: string | null } })),
        ]);
        const xeroReady = !!(xero.connected && xero.connection?.default_bank_account_id && xero.connection?.default_account_code);
        const myobReady = !!(myob.connected && myob.connection?.default_bank_account_uid && myob.connection?.default_account_uid && myob.connection?.default_tax_code_uid);
        const resumeOnboarding = !!sessionStorage.getItem('ra_onboarding_resume');
        if ((!xeroReady && !myobReady) || resumeOnboarding) {
          setNeedsOnboarding(true);
        }
        setRoute(parseHash());
        // Fetch review count for mobile tab badge
        try {
          const r = await api.listReceipts();
          const review = r.receipts.filter((rx: any) => ['pending','parsed','needs_review','confirmed','failed','duplicate'].includes(rx.status));
          setReviewCount(review.length);
        } catch { /* ignore */ }
      } catch {
        const hash = window.location.hash.replace(/^#/, '');
        const pub = parseHashPublic();
        if (pub) {
          setRoute(pub);
        } else if (hash.startsWith('/login')) {
          setRoute({ name: 'login' });
        } else {
          setRoute({ name: 'landing' });
        }
      }
      setLoading(false);
    })();

    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (loading) {
    return <div style={{ minHeight: '100vh', background: '#0A1428' }} />;
  }

  // Legal pages are accessible with or without auth. Render them without the
  // app shell so signed-out visitors can reach them straight from the footer.
  const legal = renderLegal(route);
  if (legal && !user) return legal;

  if (!user && route.name === 'signup') {
    return <Signup />;
  }

  if (!user && route.name === 'landing') {
    return (
      <Landing
        onSignUp={() => { window.location.hash = '#/signup'; }}
        onSignIn={() => { window.location.href = `${(import.meta.env.VITE_API_BASE as string | undefined) ?? ''}/api/xero/login`; }}
      />
    );
  }

  if (!user) return <Login />;

  if (needsOnboarding) {
    return <Onboarding onComplete={() => setNeedsOnboarding(false)} />;
  }

  // Signed-in visit to a legal page: render inside the app shell so they can
  // still navigate back into the product.
  if (legal) {
    return (
      <AppShell page={route.name} userEmail={user.email} reviewCount={reviewCount}>
        {legal}
      </AppShell>
    );
  }

  const pageName = route.name === 'receipt' ? 'detail' : route.name;

  return (
    <AppShell page={pageName} userEmail={user.email} reviewCount={reviewCount}>
      {route.name === 'dashboard' && <Dashboard />}
      {route.name === 'upload' && <Upload />}
      {route.name === 'review' && <ReviewInbox />}
      {route.name === 'rules' && <Rules />}
      {route.name === 'settings' && <Settings userEmailProp={user.email} />}
      {route.name === 'receipt' && <ReceiptDetail id={route.id} />}
    </AppShell>
  );
}
